import { createReachVideoHandler, Media, State } from '../../../MediaController';
import { ReachVideoHandler } from '../../../MediaController/Video';
import './playOnDemand.scss';

type VideoPlayers = Map<
    HTMLElement, // Target Element
    Map<
        string, // Video ID
        ReachVideoHandler
    >
>;

export class OnDemandVideoPlayer {
    private static videoPlayers: VideoPlayers = new Map();

    /**
     * Plays the video passed in
     * as soon as possible.
     */
    public static async playVideoOnDemand(src: string, element: HTMLElement): Promise<void> {
        const currentElementMap = await this.getOrSetCurrentElementMap(element);

        await this.stopVideosInSelectedElement(currentElementMap);

        const videoHandler = await this.getOrSetVideoHandler(
            currentElementMap,
            src,
            false,
            element
        );

        const videoContainer = videoHandler.getElement();

        videoContainer.classList.add('play-on-demand__video--active');

        await videoHandler.play();

        videoHandler.eventEmitter.addListener(Media.Ended, async () => {
            await videoHandler.stop();
            videoContainer.classList.remove('play-on-demand__video--active');
        });
    }

    /**
     * Stops any video on demand videos current playing
     * in the selected element.
     */
    private static async stopVideosInSelectedElement(
        currentElementMap: Map<string, ReachVideoHandler>
    ) {
        for (const aVideoHandler of currentElementMap.values()) {
            if (aVideoHandler.state === State.PLAYING) {
                await aVideoHandler.stop();
                const aVideoContainer = aVideoHandler.getElement();

                aVideoContainer.classList.remove('play-on-demand__video--active');
            }
        }
    }

    /**
     * Retrieves
     * or sets the video handler for the video id within the context
     * of the current element.
     */
    private static async getOrSetVideoHandler(
        currentElementMap: Map<string, ReachVideoHandler>,
        src: string,
        muted: boolean,
        element: HTMLElement
    ): Promise<ReachVideoHandler> {
        const valueInMap = currentElementMap.get(src);

        if (valueInMap) {
            return valueInMap;
        }
        const videoContainer: HTMLDivElement = document.createElement('div');
        const aVideoHandler = createReachVideoHandler({ videoUuid: src, muted });
        await aVideoHandler.setElement(videoContainer);

        element.appendChild(videoContainer);

        currentElementMap.set(src, aVideoHandler);

        videoContainer.classList.add('play-on-demand__video');
        return aVideoHandler;
    }

    /**
     * Gets or set the corresponding map
     * item for the selected html element.
     */
    private static async getOrSetCurrentElementMap(
        element: HTMLElement
    ): Promise<Map<string, ReachVideoHandler>> {
        const anElementMap = this.videoPlayers.get(element);

        if (anElementMap) {
            return anElementMap;
        }
        const newMap = new Map<string, ReachVideoHandler>();
        this.videoPlayers.set(element, newMap);

        return newMap;
    }
}
